import { Link } from 'react-router-dom';
import { LockKeyhole } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { BrandMark } from './BrandMark';

export type ProtectedRouteFallbackProps = {
  /**
   * `checking` — the session is being restored (default).
   * `denied`   — the session resolved and the user may not be here.
   */
  state?: 'checking' | 'denied';
  className?: string;
};

/**
 * What a guarded route renders while `useAuth()` has not settled, and what it renders when
 * the answer is "no". Deliberately quiet: a full-screen spinner on canvas, never a flash of
 * the app shell with empty numbers in it.
 */
export function ProtectedRouteFallback({
  state = 'checking',
  className,
}: ProtectedRouteFallbackProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'flex min-h-screen flex-col items-center justify-center gap-4 bg-canvas px-6',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <BrandMark size={28} className="text-ink" />

      {state === 'checking' ? (
        <div className="flex items-center gap-2 text-ink-tertiary">
          <Spinner size="sm" />
          <span className="text-body">{t('common.loading')}</span>
        </div>
      ) : (
        <div className="flex max-w-[360px] flex-col items-center text-center">
          <LockKeyhole
            aria-hidden="true"
            strokeWidth={1.5}
            className="mb-3 h-8 w-8 text-ink-tertiary"
          />
          <h1 className="text-heading text-ink">{t('errors.UNAUTHORIZED')}</h1>
          <p className="mt-1.5 text-body-md text-ink-secondary">{t('auth.sessionExpired')}</p>
          <Button asChild variant="primary" className="mt-4">
            <Link to="/login">{t('auth.signIn')}</Link>
          </Button>
        </div>
      )}
    </div>
  );
}
